import React from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { THEME_COLOR } from '../../../theme/formStyles';
import { BUSY_DAY_TASKS, dayRole, engineerName, initials, isOnDay, isUnassigned, monthWeeks, siteColor, todayYmd, itemKey } from '../projectSchedule.utils';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const ROLE_TAG = { start: 'Start', end: 'Ends', one: '' };
const MAX_CHIPS = 3;

function TaskChip({ task, role, onOpen, compact }) {
    const unassigned = isUnassigned(task);
    return (
        <Box onClick={(e) => { e.stopPropagation(); onOpen(task); }} title={`${task.sub_task_name} · ${task.facility_name} · ${engineerName(task)}`}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: compact ? '9px' : '11.5px', fontWeight: 500, px: compact ? 0.4 : 0.75, py: compact ? 0.15 : 0.4, borderRadius: '5px', bgcolor: '#f6f8fa', borderLeft: `${compact ? 2 : 3}px solid ${siteColor(task.facility_id)}`, whiteSpace: 'nowrap', overflow: 'hidden', minWidth: 0, cursor: 'pointer', '&:hover': { bgcolor: '#eef2f5' } }}>
            {!compact && ROLE_TAG[role] && <Box component="span" sx={{ fontSize: '9.5px', fontWeight: 700, textTransform: 'uppercase', opacity: 0.75, flex: 'none' }}>{ROLE_TAG[role]}</Box>}
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{task.sub_task_name}</Box>
            {!compact && <Box component="span" sx={{ fontSize: '10px', fontWeight: 700, flex: 'none', color: unassigned ? '#dc2626' : '#6b7785' }}>{unassigned ? '!' : initials(engineerName(task))}</Box>}
        </Box>
    );
}

// Month grid without range bars: a task shows on its start day and end day (or every day if it is 1–2 days long).
// Days in the middle of a long task are counted in the cell footer instead.
export default function TaskMonthGrid({ tasks, year, month, onOpenTask, onOpenDay }) {
    const compact = useMediaQuery('(max-width:700px)');
    const weeks = monthWeeks(year, month);
    const today = todayYmd();

    return (
        <Box sx={{ border: '1px solid #e5e9ee', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', bgcolor: '#fafbfc', borderBottom: '1px solid #e5e9ee' }}>
                {DAY_NAMES.map(d => (
                    <Typography key={d} sx={{ textAlign: 'center', py: 1, fontSize: '12.5px', fontWeight: 500, color: '#6b7785' }}>{compact ? d[0] : d}</Typography>
                ))}
            </Box>
            {weeks.map((week, wi) => (
                <Box key={wi} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))' }}>
                    {week.map((cell, ci) => {
                        const all = tasks.filter(t => isOnDay(t, cell.ymd));
                        const shown = all.filter(t => dayRole(t, cell.ymd) !== 'mid');
                        const mid = all.filter(t => dayRole(t, cell.ymd) === 'mid');
                        const unassigned = all.filter(isUnassigned).length;
                        const midSites = [...new Set(mid.map(t => t.facility_id))];
                        const isToday = cell.ymd === today;
                        const weekend = cell.weekday === 0 || cell.weekday === 6;
                        return (
                            <Box key={cell.ymd} onClick={() => onOpenDay(cell.ymd)}
                                sx={{
                                    minHeight: compact ? 86 : 132, p: compact ? '4px 2px' : '6px', minWidth: 0, cursor: 'pointer',
                                    display: 'flex', flexDirection: 'column', gap: '3px',
                                    borderRight: ci < 6 ? '1px solid #e5e9ee' : 'none', borderBottom: wi < weeks.length - 1 ? '1px solid #e5e9ee' : 'none',
                                    bgcolor: isToday ? '#f3fbf9' : !cell.inMonth ? '#fafbfc' : weekend ? '#fcfcfd' : '#fff',
                                    '&:hover': { bgcolor: '#f7fafc' },
                                }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '2px' }}>
                                    <Typography component="span" sx={{
                                        fontSize: '13px', fontWeight: 500, color: cell.inMonth ? '#1f2933' : '#b4bcc6',
                                        ...(isToday && { bgcolor: THEME_COLOR, color: '#fff', borderRadius: 99, px: 0.9 }),
                                    }}>
                                        {cell.day}
                                    </Typography>
                                    {all.length > 0 && (
                                        <Box component="span" title={`${all.length} sub-tasks`}
                                            sx={{ fontSize: '10.5px', fontWeight: 700, borderRadius: 99, px: 0.9, bgcolor: all.length >= BUSY_DAY_TASKS ? '#fff1e6' : '#eef1f4', color: all.length >= BUSY_DAY_TASKS ? '#c2410c' : '#6b7785' }}>
                                            {all.length}
                                        </Box>
                                    )}
                                </Box>
                                {shown.slice(0, MAX_CHIPS).map(t => (
                                    <TaskChip key={itemKey(t)} task={t} role={dayRole(t, cell.ymd)} onOpen={onOpenTask} compact={compact} />
                                ))}
                                {shown.length > MAX_CHIPS && (
                                    <Typography sx={{ fontSize: compact ? '9px' : '11px', color: THEME_COLOR, fontWeight: 500 }}>+{shown.length - MAX_CHIPS} more</Typography>
                                )}
                                {(mid.length > 0 || unassigned > 0) && (
                                    <Box sx={{ mt: 'auto', pt: '3px', display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', fontSize: compact ? '10px' : '11px', color: '#6b7785', justifyContent: compact ? 'center' : 'flex-start' }}>
                                        {mid.length > 0 && (
                                            <>
                                                <Box component="span" sx={{ display: 'inline-flex', gap: '2px' }}>
                                                    {midSites.map(k => <Box key={k} component="i" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: siteColor(k) }} />)}
                                                </Box>
                                                {!compact && <span>{mid.length} ongoing</span>}
                                            </>
                                        )}
                                        {unassigned > 0 && (
                                            <Box component="span" sx={{ color: '#dc2626', fontWeight: 700 }}>! {unassigned}{!compact && ' unassigned'}</Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
}
